import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MessagesSquare } from "lucide-react";
import { ITEM_ICON, ITEM_LABEL } from "@/pages/student/itemTypes";

export type ThreadItem = {
  activityId: string;
  activityTitle: string;
  activityType: string;
  courseId: string;
  courseTitle: string;
  lastAt: string | null;
  last: {
    body: string;
    feedback: boolean;
    authorRole: string;
    authorName: string | null;
    mine: boolean;
  };
  studentPosted: boolean;
  teacherFeedback: boolean;
  viewerPosted: boolean;
  messageCount: number;
  studentUserId?: string | null;
  studentName?: string | null;
  kind?: "activity" | "student";
};

export type ThreadFilter = {
  id: string;
  label: string;
  test: (item: ThreadItem) => boolean;
};

function whenLabel(iso: string | null) {
  if (!iso) return "";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  if (days < 1 && then.getDate() === new Date().getDate()) {
    return then.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  if (days < 7) return then.toLocaleDateString(undefined, { weekday: "short" });
  return then.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(then.getFullYear() === new Date().getFullYear() ? {} : { year: "numeric" as const }),
  });
}

function whoLabel(item: ThreadItem) {
  const last = item.last;
  if (last.feedback) return "Teacher feedback";
  if (last.mine) return "You";
  if (last.authorRole === "parent") return last.authorName ? `${last.authorName} (parent)` : "Parent";
  if (last.authorRole === "counselor") return last.authorName ? `${last.authorName} (counselor)` : "Counselor";
  if (last.authorName) return last.authorName;
  return last.authorRole === "learner" ? "Student" : "Teacher";
}

export default function ThreadList({
  items,
  filters,
  hrefFor,
  showStudent = false,
  emptyTitle,
  emptyText,
}: {
  items: ThreadItem[];
  filters: ThreadFilter[];
  hrefFor: (item: ThreadItem) => string;
  showStudent?: boolean;
  emptyTitle: string;
  emptyText: string;
}) {
  const [filterId, setFilterId] = useState(filters[0]?.id ?? "all");
  const active = filters.find((f) => f.id === filterId) ?? filters[0];
  const shown = useMemo(() => (active ? items.filter(active.test) : items), [items, active]);

  return (
    <>
      <div className="student-thread-filters" role="group" aria-label="Show">
        {filters.map((filter) => (
          <button
            key={filter.id}
            type="button"
            className={`student-chip${filter.id === active?.id ? " student-chip--accent" : ""}`}
            aria-pressed={filter.id === active?.id}
            onClick={() => setFilterId(filter.id)}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="student-empty">
          <div>
            <strong>{items.length === 0 ? emptyTitle : "Nothing here"}</strong>
            <p>{items.length === 0 ? emptyText : "Try another filter."}</p>
          </div>
        </div>
      ) : (
        <section className="student-card student-thread-list">
          {shown.map((item) => {
            const Icon = ITEM_ICON[item.activityType] ?? MessagesSquare;
            return (
              <Link
                key={`${item.studentUserId ?? ""}:${item.activityId}`}
                className="student-row student-thread-row"
                to={hrefFor(item)}
              >
                <span className="student-thread-row__icon" aria-hidden>
                  <Icon size={16} />
                </span>
                <span className="student-thread-row__main">
                  <strong>{item.activityTitle}</strong>
                  <span className="student-row__sub">
                    {showStudent && item.studentName ? `${item.studentName} · ` : ""}
                    {item.courseTitle}
                    {ITEM_LABEL[item.activityType] ? ` · ${ITEM_LABEL[item.activityType]}` : ""}
                  </span>
                  {item.last.body ? (
                    <span className="student-thread-row__snippet">
                      <span className="student-thread-row__who">{whoLabel(item)}:</span> {item.last.body}
                    </span>
                  ) : null}
                </span>
                <time className="student-thread-row__when" dateTime={item.lastAt ?? undefined}>
                  {whenLabel(item.lastAt)}
                </time>
              </Link>
            );
          })}
        </section>
      )}
    </>
  );
}
